import type {
  OrganisationCardPaymentStatus,
  OrganisationSpendCapacity,
} from '@dreamai/types';

export const CARD_STATUS_LABELS: Record<OrganisationCardPaymentStatus, string> =
  {
    pending_payment: 'Pending',
    paid: 'Pending',
    card_issued: 'Pending',
    settling: 'Pending',
    settled: 'Done',
    cancelled: 'Cancelled',
    failed: 'Payment failed',
  };

/** Statuses whose quoted amount still counts against the cycle cap. */
export const OPEN_CARD_STATUSES: ReadonlySet<OrganisationCardPaymentStatus> =
  new Set<OrganisationCardPaymentStatus>([
    'pending_payment',
    'paid',
    'card_issued',
    'settling',
  ]);

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/** Empty string ↔ null ("no cap"); undefined when invalid. */
export function parseOptionalMoney(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100) / 100;
}

export function capLabel(cap: number | null, currency: string): string {
  return cap === null ? 'No cap' : formatMoney(cap, currency);
}

/**
 * One-line summary of a member's organisation-funded headroom, for the
 * member view. Never mentions balances or top-ups: capacity is set by admins.
 */
export function describeCapacity(capacity: OrganisationSpendCapacity): string {
  if (capacity.cycleCap === null && capacity.perTransactionCap === null) {
    return `No spending limit set by ${capacity.organisationName}.`;
  }
  const parts: string[] = [];
  if (capacity.perTransactionCap !== null) {
    parts.push(
      `up to ${formatMoney(capacity.perTransactionCap, capacity.currency)} per purchase`,
    );
  }
  if (capacity.cycleCap !== null && capacity.cycleRemaining !== null) {
    parts.push(
      `${formatMoney(capacity.cycleRemaining, capacity.currency)} of ${formatMoney(capacity.cycleCap, capacity.currency)} left this month`,
    );
  }
  return parts.join(', ') + '.';
}
